import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip,
  IconButton,
  Breadcrumbs,
  Link as MuiLink,
  Divider,
  useTheme,
  useMediaQuery,
  Skeleton,
  Alert,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  ThumbUp,
  ThumbUpOutlined,
  AccessTime,
  Cake,
  Public,
  VisibilityOff,
  NavigateNext,
  SportsEsports,
  Lock,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { UserMemory } from '../types/Memory';
import { UserProfile } from '../types/UserProfile';
import { getMemoryDetail, toggleMemoryLike, getUserProfileByUsername } from '../services/api';
import { formatReviewText } from '../utils/formatText';
import { calculateAgeAtPlay, calculateYearsAgo } from '../utils/dateUtils';
import ShareButtons from '../components/common/ShareButtons';

const MemoryDetailPage: React.FC = () => {
  const { gameId, memoryId } = useParams<{ gameId: string; memoryId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [memory, setMemory] = useState<UserMemory | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [userLikeCount, setUserLikeCount] = useState(0);

  useEffect(() => {
    const fetchMemoryDetail = async () => {
      if (!memoryId) return;
      
      try {
        setLoading(true);
        const response = await getMemoryDetail(parseInt(memoryId));
        const memoryData = response.data;
        setMemory(memoryData);
        setIsLiked(memoryData.user_liked || false);
        setLikeCount(memoryData.like_count || 0);
        setUserLikeCount(memoryData.user_like_count || 0);

        // ユーザープロフィール情報を取得（年齢表示のため）
        if (memoryData.username && !memoryData.is_anonymous) {
          try {
            const profileData = await getUserProfileByUsername(memoryData.username);
            setUserProfile(profileData);
          } catch (profileError) {
            console.warn('プロフィール情報の取得に失敗しました:', profileError);
          }
        }
      } catch (err: any) {
        console.error('メモリー詳細の取得に失敗しました:', err);
        setError(err.response?.data?.error || 'メモリーの読み込みに失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchMemoryDetail();
  }, [memoryId]);

  const handleLikeClick = async () => {
    if (!memory) return;
    
    if (!localStorage.getItem('token')) {
      navigate('/login');
      return;
    }

    try {
      if (userLikeCount >= 10) {
        console.error('いいねの上限（10回）に達しています。');
        return;
      }

      const response = await toggleMemoryLike(memory.id);
      setIsLiked(true);
      setLikeCount(response.data.total_likes);
      setUserLikeCount(response.data.user_like_count);
    } catch (error: any) {
      console.error('いいね操作に失敗しました:', error);
    }
  };

  const getCoverUrl = (coverImage: string | null) => {
    if (!coverImage) return '/images/no-cover.png';
    
    const coverType = coverImage.startsWith('pulse_') ? 'pulse' : 'igdb';
    const imageId = coverImage.replace('pulse_', '');
    
    if (coverType === 'pulse') {
      return `https://images.igdb.com/igdb/image/upload/t_cover_big/${imageId}.jpg`;
    } else {
      return `https://images.igdb.com/igdb/image/upload/t_cover_big/${coverImage}.jpg`;
    }
  };

  const formatPlayDate = (memory: UserMemory) => {
    let dateToUse = null;
    if (memory.status_play_end_date) {
      dateToUse = memory.status_play_end_date;
    } else if (memory.status_play_start_date) {
      dateToUse = memory.status_play_start_date;
    } else if (memory.play_date) {
      dateToUse = memory.play_date;
    }

    if (!dateToUse) return null;
    
    const playDate = new Date(dateToUse);
    const yearsAgo = calculateYearsAgo(dateToUse);
    
    let ageAtPlay = null;
    
    if (userProfile?.birth_date && userProfile.age_privacy > 0) {
      const exactAge = calculateAgeAtPlay(userProfile.birth_date, dateToUse);
      
      if (exactAge !== null) {
        if (userProfile.age_privacy === 2) {
          if (exactAge >= 6 && exactAge <= 12) {
            const grade = exactAge - 5;
            ageAtPlay = `小学${grade}年生`;
          } else if (exactAge >= 13 && exactAge <= 15) {
            const grade = exactAge - 12;
            ageAtPlay = `中学${grade}年生`;
          } else {
            ageAtPlay = exactAge;
          }
        } else if (userProfile.age_privacy === 1) {
          if (exactAge >= 6 && exactAge <= 12) {
            ageAtPlay = '小学生';
          } else if (exactAge >= 13 && exactAge <= 15) {
            ageAtPlay = '中学生';
          } else {
            const decade = Math.floor(exactAge / 10) * 10;
            const isFirstHalf = exactAge % 10 < 5;
            ageAtPlay = `${decade}代${isFirstHalf ? '前半' : '後半'}`;
          }
        }
      }
    }

    const year = playDate.getFullYear();
    const month = playDate.getMonth() + 1;
    const day = playDate.getDate();

         return {
       fullDate: `${year}年${month}月`,
       yearsAgo,
       ageAtPlay,
       actualPlayDate: dateToUse
     };
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Skeleton variant="rectangular" width={120} height={160} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={300} height={30} />
                <Skeleton variant="text" width={200} height={25} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="100%" height={20} />
                <Skeleton variant="text" width="100%" height={20} />
                <Skeleton variant="text" width="80%" height={20} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (error || !memory) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'メモリーが見つかりません'}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
        >
          戻る
        </Button>
      </Container>
    );
  }

  const playDateInfo = formatPlayDate(memory);

  return (
    <>
      <Helmet>
        <title>{`${memory.title} - ${memory.game.name}の思い出 | PULSE`}</title>
        <meta name="description" content={memory.content.slice(0, 160)} />
      </Helmet>

      <Container maxWidth="sm" sx={{ py: 4 }}>
        {/* パンくずリスト */}
        <Breadcrumbs
          separator={<NavigateNext fontSize="small" />}
          sx={{ mb: 3, color: 'text.secondary', fontSize: { xs: '0.7rem', md: '0.8rem' } }}
        >
          <MuiLink component={RouterLink} to={`/game/${memory.game.id}`} color="inherit">
            {memory.game.name}
          </MuiLink>
          <Typography color="text.secondary" sx={{ fontSize: { xs: '0.7rem', md: '0.8rem' } }}>メモリー</Typography>
        </Breadcrumbs>

        <Paper
          elevation={1}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.01)',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {/* ヘッダー */}
          <Box sx={{ p: { xs: 1.5, md: 3 }, pb: 0 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              {/* ゲームカバー */}
              <Box sx={{ flexShrink: 0 }}>
                <RouterLink to={`/game/${memory.game.id}`}>
                  <Box
                    component="img"
                    src={getCoverUrl(memory.game.cover_id)}
                    alt={memory.game.name}
                    sx={{
                      width: { xs: 90, md: 120 },
                      height: { xs: 120, md: 160 },
                      borderRadius: 1,
                      objectFit: 'cover',
                      cursor: 'pointer',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
                      }
                    }}
                  />
                </RouterLink>
              </Box>

              {/* メタ情報 */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* ユーザー情報 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                     <Avatar
                     src={memory.is_anonymous ? undefined : memory.avatar || undefined}
                     sx={{ 
                       width: 40, 
                       height: 40,
                       border: '1px solid rgba(255, 255, 255, 0.15)',
                     }}
                   >
                     {memory.is_anonymous ? '?' : memory.display_name?.[0] || memory.username[0]}
                   </Avatar>
                  
                   <Box>
                     <Typography variant="body1" sx={{ color: '#fff', fontWeight: 500, fontSize: { xs: '0.7rem', md: '0.8rem' } }}>
                       {memory.is_anonymous ? '匿名ユーザー' : (
                         <MuiLink 
                           component={RouterLink} 
                           to={`/profile/${memory.username}`}
                           sx={{
                             color: 'inherit',
                             textDecoration: 'none',
                             '&:hover': {
                               color: 'primary.main',
                               textDecoration: 'underline'
                             }
                           }}
                         >
                           {memory.display_name || memory.username}
                         </MuiLink>
                       )}
                     </Typography>
                     
                     <MuiLink 
                       component={RouterLink} 
                       to={`/game/${memory.game.id}`}
                       sx={{
                         color: 'text.secondary',
                         textDecoration: 'none',
                         fontSize: { xs: '0.7rem', md: '0.8rem' },
                         lineHeight: 1,
                         display: 'flex',
                         alignItems: 'center',
                         mt: 0.5,
                         '&:hover': {
                           color: 'primary.light',
                           textDecoration: 'underline'
                         }
                       }}
                     >
                       {memory.game.name}
                     </MuiLink>
                   </Box>
                </Box>

                {/* プレイ日付情報 */}
                {playDateInfo && (
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap',
                    alignItems: 'center', 
                    gap: 1, 
                    mb: 2
                  }}>
                    <Tooltip title={`プレイ日: ${playDateInfo.fullDate}`} arrow>
                                             <Chip
                         icon={<AccessTime />}
                         label={`${playDateInfo.fullDate}${playDateInfo.yearsAgo > 1 ? ` (${playDateInfo.yearsAgo}年前)` : ''}`}
                         size="small"
                         sx={{
                           background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.2), rgba(33, 150, 243, 0.3))',
                           color: '#90CAF9',
                           border: '1px solid rgba(33, 150, 243, 0.4)',
                           borderRadius: '4px',
                           fontWeight: 500,
                           fontSize: '0.7rem',
                           '& .MuiChip-icon': {
                             color: '#90CAF9',
                             fontSize: '0.8rem'
                           }
                         }}
                       />
                    </Tooltip>

                    {playDateInfo.ageAtPlay !== null && (
                      <Tooltip title="プレイ時の年齢" arrow>
                                                 <Chip
                           icon={<Cake />}
                           label={typeof playDateInfo.ageAtPlay === 'string' ? playDateInfo.ageAtPlay : `${playDateInfo.ageAtPlay}歳`}
                           size="small"
                           sx={{
                             background: 'linear-gradient(45deg, rgba(255, 152, 0, 0.2), rgba(255, 152, 0, 0.3))',
                             color: '#FFB74D',
                             border: '1px solid rgba(255, 152, 0, 0.4)',
                             borderRadius: '4px',
                             fontWeight: 500,
                             fontSize: '0.7rem',
                             '& .MuiChip-icon': {
                               color: '#FFB74D',
                               fontSize: '0.8rem'
                             }
                           }}
                         />
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />

          {/* メイン内容 */}
          <Box sx={{ p: { xs: 2, md: 3 } }}>
                         {/* タイトル */}
             <Typography 
               variant="h6" 
               component="h1"
               sx={{ 
                 mb: 3, 
                 fontWeight: 500,
                 fontSize: { xs: '0.85rem', md: '0.9rem' },
                 color: '#fff',
                 lineHeight: 1.3
               }}
             >
               {memory.title}
             </Typography>

                         {/* 内容 */}
             <Box sx={{ 
               mb: 3,
               color: 'text.primary',
               '& .spoiler': {
                 backgroundColor: '#444',
                 padding: '0 4px',
                 borderRadius: '2px',
                 cursor: 'pointer',
                 '&:hover': {
                   backgroundColor: 'transparent'
                 }
               }
             }}>
               <Typography variant="body2" color="pulse.text" sx={{ lineHeight: { xs: 1.35, sm: 1.4 } }}>
                 {formatReviewText(memory.content, isMobile)}
               </Typography>
             </Box>

            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)', mb: 2 }} />

            {/* フッター */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2
            }}>
                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                 <Button
                   size="small"
                   startIcon={isLiked ? <ThumbUp /> : <ThumbUpOutlined />}
                   onClick={handleLikeClick}
                   disabled={userLikeCount >= 10}
                   color={isLiked ? "primary" : "inherit"}
                   sx={{ 
                     boxShadow: 'none',
                     '&:hover': {
                       boxShadow: 'none',
                     },
                     '&.Mui-disabled': {
                       opacity: 0.6,
                       color: '#F48FB1'
                     }
                   }}
                 >
                   {likeCount}
                   {userLikeCount > 0 && ` (+${userLikeCount})`}
                 </Button>
                 <ShareButtons
                   text={`『${memory.game.name}』の思い出${memory.title ? `：${memory.title}` : ''}${!memory.is_anonymous ? ` by ${memory.display_name || memory.username}` : ''}`}
                   url={`/game/${memory.game.id}/memories/${memory.id}`}
                   hashtags={['PULSE_DB']}
                   buttonText='共有'
                 />
               </Box>

                             <Typography 
                 variant="body2" 
                 sx={{ 
                   color: 'pulse.info',
                   fontSize: '0.7rem'
                 }}
               >
                 投稿日：{new Date(memory.created_at).toLocaleDateString('ja-JP', {
                   year: 'numeric',
                   month: '2-digit',
                   day: '2-digit'
                 })}
               </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default MemoryDetailPage; 